import React from 'react';
import { useTranslation } from 'react-i18next';
import Seo from "../seo/Seo";
import CtaPair from "../components/CtaPair";
import { FaFileAlt, FaSyncAlt, FaChalkboardTeacher, FaArrowRight, FaToolbox, FaMapMarkerAlt } from "react-icons/fa";
import { Link } from "../i18n/LangContext";

// Project Images
import projectImage1 from '../assets/images/about-project-content-pipeline.jpg';
import projectImage2 from '../assets/images/about-project-document-automation.jpg';
import projectImage3 from '../assets/images/about-project-data-platform.jpg';
import chartImage from '../assets/images/about-project-geospatial.jpg';
import teamMember1 from '../assets/images/csuvik.png';
import teamMember2 from '../assets/images/vajer.png';
import teamMember3 from '../assets/images/simon.png';
import teamMember4 from '../assets/images/Jonathan.png';
import heroImage from '../assets/images/about-hero.jpg';

const About = () => {

  const { t } = useTranslation();

  // Paired positionally with about.services in the locale files.
  const SERVICE_ICONS = [
    <FaFileAlt className="service-icon" />,
    <FaSyncAlt className="service-icon" />,
    <FaChalkboardTeacher className="service-icon" />
  ];
  const services = t('about.services').map((svc, i) => ({ ...svc, icon: SERVICE_ICONS[i] }));

  const PROJECT_IMAGES = [projectImage1, projectImage2, projectImage3, chartImage];
  const projects = t('about.projects').map((prj, i) => ({ ...prj, image: PROJECT_IMAGES[i] }));

  // Photos only; the names live in about.team.members so each language can use
  // its own name order (Hungarian puts the family name first).
  const TEAM = [teamMember1, teamMember2, teamMember3, teamMember4];
  const team = t('about.team.members').map((m, i) => ({ ...m, img: TEAM[i] }));
  const facts = t('about.facts');
  const faq = t('about.faq');
  const faqJsonLd = {
    "@type": "FAQPage",
    mainEntity: faq.items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };

  return (
    <div>
      <Seo page="about" path="/about" override={{ jsonLd: [faqJsonLd] }} />
      {/* Hero */}
      <section className="about-hero">
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-lg-6">
              <span className="about-hero-eyebrow">{t('about.hero.eyebrow')}</span>
              <h1 className="about-hero-title">{t('about.hero.title')}</h1>
              <p className="about-hero-text">{t('about.hero.text')}</p>
              <CtaPair tone="dark" />
            </div>
            <div className="col-lg-6">
              <img src={heroImage} alt={t('about.hero.title')} className="about-hero-img" />
            </div>
          </div>
        </div>
      </section>

      {/* Credibility strip */}
      <section className="about-stats">
        <div className="container">
          <div className="row g-4 text-center">
            {t('about.stats').map((stat, i) => (
              <div key={i} className="col-6 col-lg-3">
                <div className="about-stat">
                  <span className="about-stat-value">{stat.v}</span>
                  <span className="about-stat-label">{stat.l}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* One office, and we say where. Never imply another location. */}
      <section className="about-location">
        <div className="container">
          <p>
            <FaMapMarkerAlt aria-hidden="true" />
            <span className="about-location-label">{t('about.location.label')}:</span>
            {' '}{t('about.location.text')}
          </p>
        </div>
      </section>

      {/* Who we are - plain facts for readers, search engines and AI assistants */}
      <section className="about-facts py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="p-4 p-md-5 bg-white rounded-4 shadow-sm border-left-highlight">
                <h2 className="h3 font-weight-bold mb-3">{facts.title}</h2>
                <p className="lead text-muted mb-0 lh-lg">{facts.text}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="work-services py-5 bg-light">
        <div className="container">
          <div className="heading_container d-flex justify-content-center mb-5">
            <h2>{t('about.servicesTitle')}</h2>
          </div>
          <p className="about-services-lead">{t('about.servicesLead')}</p>
          <div className="row g-4">
            {services.map((service, index) => (
              <div key={index} className="col-lg-4 col-md-6 mb-4">
                <div className="modern-service-card h-100 p-4 shadow-sm border-0 rounded-4 transition-hover d-flex flex-column">
                  <div className="icon-wrapper mb-3 icon-gradient-bg rounded-circle d-inline-flex align-items-center justify-content-center p-3 yolo-pulse">
                    {service.icon}
                  </div>
                  <h4 className="font-weight-bold mb-3">{service.title}</h4>
                  <p className="text-muted lh-lg flex-grow-1">{service.description}</p>
                  <Link to={service.link} className="cta-inline align-self-start">
                    {service.linkLabel} <FaArrowRight />
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <div className="about-toolbox">
            <h3><FaToolbox aria-hidden="true" /> {t('about.toolbox.title')}</h3>
            <p>{t('about.toolbox.text')}</p>
          </div>
        </div>
      </section>

      {/* Featured Projects Section */}
      <section className="work-projects py-5">
        <div className="container">
          <div className="heading_container d-flex justify-content-center mb-5">
            <h2>{t('about.projectsTitle')}</h2>
          </div>
          <div className="row g-4">
            {projects.map((project, index) => (
              <div key={index} className="col-md-6">
                <article className="project-highlight-card bg-white rounded-4 shadow-sm h-100 transition-hover overflow-hidden">
                  <div className="project-card-media">
                    <img src={project.image} alt={project.title} loading="lazy" />
                    <div className="project-card-tags">
                      {project.tags.map((tag, tagIndex) => (
                        <span key={tagIndex} className="project-card-tag">{tag}</span>
                      ))}
                    </div>
                  </div>
                  <div className="p-4">
                    <h4 className="font-weight-bold mb-3">{project.title}</h4>
                    <p className="text-muted mb-0 lh-lg">{project.description}</p>
                  </div>
                </article>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="about-faq py-5">
        <div className="container">
          <div className="heading_container d-flex justify-content-center mb-5">
            <h2>{faq.title}</h2>
          </div>
          <div className="row justify-content-center">
            <div className="col-lg-9">
              {faq.items.map((item, i) => (
                <details key={i} className="faq-item modern-service-card rounded-4 shadow-sm mb-3 p-4">
                  <summary className="h5 font-weight-bold mb-0" style={{ cursor: 'pointer' }}>{item.q}</summary>
                  <p className="text-muted mt-3 mb-0 lh-lg">{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="team-members_section py-5 bg-light">
        <div className="container">
          <div className="row justify-content-center text-center mb-5">
            <div className="col-lg-8">
              <div className="heading_container d-flex justify-content-center">
                <h2>{t('about.team.title')}</h2>
              </div>
              <p className="text-muted mt-3">
                {t('about.team.text')}
              </p>
            </div>
          </div>
          <div className="row g-4 justify-content-center">
            {team.map((member) => (
              <div key={member.name} className="col-md-6 col-lg-3">
                <div className="team-card modern-service-card p-4 text-center rounded-4 transition-hover h-100">
                  <img
                    src={member.img}
                    alt={member.name}
                    className="rounded-circle mb-3 shadow-sm yolo-cyber-pop"
                    style={{ width: '120px', height: '120px', objectFit: 'cover' }}
                  />
                  <h4 className="mb-1 font-weight-bold">{member.name}</h4>
                  <p className="text-primary mb-3">{member.role}</p>
                  <p className="text-muted small">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;